import React from "react";
import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowUpOnSquareIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BoltIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  ExclamationCircleIcon,
  FaceSmileIcon,
  FireIcon,
  GiftTopIcon,
  GlobeAmericasIcon,
  IdentificationIcon,
  InformationCircleIcon,
  KeyIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MinusCircleIcon,
  MoonIcon,
  PlayCircleIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Squares2X2Icon,
  SunIcon,
  TrophyIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  WalletIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import {
  ClockIcon as ClockOutlineIcon,
  GiftIcon as GiftOutlineIcon,
  ShieldCheckIcon as ShieldCheckOutlineIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

export {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowUpOnSquareIcon,
  BanknotesIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  GiftTopIcon,
  IdentificationIcon,
  InformationCircleIcon,
  KeyIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  PlayCircleIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Squares2X2Icon,
  SunIcon,
  TrophyIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  WalletIcon,
  XCircleIcon,
  XMarkIcon,
};

const joinClasses = (...classes) => classes.filter(Boolean).join(" ");

const SPORT_ICON_GROUPS = {
  trophy: new Set([
    "nba",
    "nba2h",
    "march",
    "cbb",
    "cbb2h",
    "wnba",
    "wnba1h",
    "wcbb",
    "3pt",
    "mlb",
    "kbo",
    "wbc",
    "nhl",
    "cfb",
    "pga",
    "soccer",
    "worldcup",
    "worldcup1h",
    "worldcup2h",
    "tennis",
    "darts",
    "boxing",
    "allstar",
    "allstarmlb",
    "hrderby",
    "cfl",
    "cricket",
    "tt",
    "celebgolf",
    "tbt",
    "olympics",
    "ohockey",
    "bowling",
    "nbasl",
    "fpa",
    "fiba",
    "celeb",
    "unr",
  ]),
  shield: new Set(["nfl", "nflp", "nflszn"]),
  fire: new Set(["ufc"]),
  chip: new Set(["cs2", "lol", "val", "cod", "dota2", "r6"]),
  rocket: new Set(["f1", "nascar"]),
  globe: new Set(["hotdog"]),
};

const SPORT_ICON_BY_GROUP = {
  trophy: TrophyIcon,
  shield: ShieldCheckIcon,
  fire: FireIcon,
  chip: CpuChipIcon,
  rocket: RocketLaunchIcon,
  globe: GlobeAmericasIcon,
};

export const getPromoToken = (athlete) => {
  if (athlete?.easy) {
    return "easy";
  }

  if (athlete?.hard) {
    return "hard";
  }

  if (athlete?.promo) {
    return "promo";
  }

  return "";
};

export const getPromoLabel = (token) => {
  if (token === "easy") {
    return "Safer";
  }

  if (token === "hard") {
    return "Boosted";
  }

  if (token === "promo") {
    return "Promo";
  }

  return "";
};

const PROMO_ICON_BY_TOKEN = {
  easy: ShieldCheckOutlineIcon,
  hard: RocketLaunchIcon,
  promo: GiftOutlineIcon,
};

export function PromoBadgeIcon({ token, className = "", title }) {
  const Icon = PROMO_ICON_BY_TOKEN[token];

  if (!Icon) {
    return null;
  }

  return (
    <Icon
      role="img"
      aria-label={title || getPromoLabel(token)}
      className={joinClasses("ww-promo-icon", `ww-promo-icon--${token}`, className)}
      title={title}
    />
  );
}

export function SelectionDirectionIcon({ direction, className = "", title }) {
  const isLess = direction === "less";

  return (
    <svg
      aria-hidden={title ? undefined : "true"}
      className={className}
      fill="none"
      focusable="false"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.75"
      style={{ transform: "translateY(-0.07em)" }}
      title={title}
      viewBox="0 0 20 20"
    >
      {isLess ? (
        <>
          <path d="M10 4.5v11" />
          <path d="M5.5 11l4.5 4.5 4.5-4.5" />
        </>
      ) : (
        <>
          <path d="M10 15.5v-11" />
          <path d="M5.5 9l4.5-4.5 4.5 4.5" />
        </>
      )}
    </svg>
  );
}

export function OutcomeToneIcon({ tone, className = "", title }) {
  const icons = {
    miss: XCircleIcon,
    draw: ArrowsRightLeftIcon,
    void: MinusCircleIcon,
    hit: CheckCircleIcon,
    pending: ClockOutlineIcon,
  };
  const Icon = icons[tone] || ClockOutlineIcon;

  return <Icon aria-hidden={title ? undefined : "true"} className={className} title={title} />;
}

export function SportIcon({ sport, className = "", title }) {
  const normalizedSport = String(sport || "").trim().toLowerCase();
  const groupEntry = Object.entries(SPORT_ICON_GROUPS).find(([, values]) => values.has(normalizedSport));
  const Icon = groupEntry ? SPORT_ICON_BY_GROUP[groupEntry[0]] : TrophyIcon;

  return <Icon aria-hidden={title ? undefined : "true"} className={className} title={title} />;
}

export function ExpandStateIcon({ expanded = false, className = "" }) {
  const Icon = expanded ? MinusCircleIcon : PlusCircleIcon;

  return <Icon aria-hidden="true" className={className} />;
}

export function ThemeModeIcon({ darkMode = true, className = "" }) {
  const Icon = darkMode ? SunIcon : MoonIcon;

  return <Icon aria-hidden="true" className={className} />;
}

export const utilityIcons = {
  money: BanknotesIcon,
  rules: PlayCircleIcon,
  search: MagnifyingGlassIcon,
  share: ArrowUpOnSquareIcon,
  support: ChatBubbleLeftRightIcon,
  swap: ArrowPathIcon,
  warning: ExclamationCircleIcon,
};
